import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, isBefore, startOfDay } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarIcon, Upload, X, Info, Loader2, CalendarDays, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import { FloatWrapper, FloatField } from "@/components/ui/FormField";
import { useDateLocale } from "@/hooks/useDateLocale";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetLeaveTypes,
  apiSubmitLeaveRequest,
  apiGetLeaveDocumentRequirement,
  apiGetAllPublicHolidays,
  apiGetLeaveReplacementCandidates,
  apiGetMyLeaveReplacementAssignments,
  type LeaveBalance,
} from "@/lib/api";
import { getLeaveApiErrorMessage } from "@/lib/leave-i18n";
import { countWorkingDays, expandObservedMondays } from "@/lib/working-days";

// ── Schema ────────────────────────────────────────────────────────────────────

const formSchema = z
  .object({
    leaveType: z.string().min(1, "Leave type is required"),
    startDate: z.date({ required_error: "Start date is required" }),
    endDate: z.date({ required_error: "End date is required" }),
    replacementEmployeeId: z.string().optional(),
    notes: z.string().max(500).optional(),
  })
  .refine((d) => !isBefore(d.endDate, d.startDate), {
    message: "End date must be on or after start date",
    path: ["endDate"],
  });

type FormValues = z.infer<typeof formSchema>;

// ── Props ─────────────────────────────────────────────────────────────────────

interface LeaveRequestDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  balances: LeaveBalance[];
  currentEmployeeId?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function LeaveRequestDialog({
  open,
  onOpenChange,
  balances,
  currentEmployeeId,
}: LeaveRequestDialogProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  // ── Data queries ────────────────────────────────────────────────────────────

  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: apiGetLeaveTypes,
    enabled: open,
  });

  const { data: allHolidays = [] } = useQuery({
    queryKey: ["publicHolidaysAll"],
    queryFn: () => apiGetAllPublicHolidays(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const expandedHolidayDates = expandObservedMondays(allHolidays);

  // ── Form ────────────────────────────────────────────────────────────────────

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      leaveType: "",
      replacementEmployeeId: undefined,
      notes: "",
    },
  });

  const { watch, reset, register, control, setValue, formState: { errors } } = form;
  const watchedType = watch("leaveType");
  const watchedStart = watch("startDate");
  const watchedEnd = watch("endDate");
  const watchedReplacementEmployeeId = watch("replacementEmployeeId");
  const startDateIso = watchedStart ? format(watchedStart, "yyyy-MM-dd") : "";
  const endDateIso = watchedEnd ? format(watchedEnd, "yyyy-MM-dd") : "";
  const hasValidDateRange = Boolean(
    watchedStart && watchedEnd && !isBefore(watchedEnd, watchedStart),
  );

  const { data: documentRequirement } = useQuery({
    queryKey: ["leaveDocumentRequirement", watchedType, startDateIso, endDateIso],
    queryFn: () =>
      apiGetLeaveDocumentRequirement({
        leaveType: watchedType,
        startDate: startDateIso,
        endDate: endDateIso,
      }),
    enabled: open && Boolean(watchedType && startDateIso && endDateIso),
  });

  const {
    data: replacementCandidates = [],
    isFetching: replacementCandidatesFetching,
    isSuccess: replacementCandidatesLoaded,
  } = useQuery({
    queryKey: ["leaveReplacementCandidates", startDateIso, endDateIso],
    queryFn: () =>
      apiGetLeaveReplacementCandidates({
        startDate: hasValidDateRange ? startDateIso : undefined,
        endDate: hasValidDateRange ? endDateIso : undefined,
      }),
    enabled: open,
    staleTime: 30_000,
  });

  const { data: overlappingReplacementAssignments = [] } = useQuery({
    queryKey: ["myLeaveReplacementAssignments", "overlap", startDateIso, endDateIso],
    queryFn: () =>
      apiGetMyLeaveReplacementAssignments({
        startDate: startDateIso,
        endDate: endDateIso,
      }),
    enabled: open && hasValidDateRange,
    staleTime: 30_000,
  });

  // ── Derived state ───────────────────────────────────────────────────────────

  const balanceForType = balances.find((b) => b.leaveType === watchedType);
  const isBalanceBasedSelectedType =
    balanceForType?.countsAgainstBalance && watchedType !== "special";

  const workingDays =
    documentRequirement?.workingDays ??
    (watchedStart && watchedEnd
      ? countWorkingDays(watchedStart, watchedEnd, expandedHolidayDates)
      : 0);

  const availableAfter =
    balanceForType && isBalanceBasedSelectedType
      ? (balanceForType.availableNow ?? 0) - workingDays
      : null;

  const balanceExceeded =
    availableAfter !== null && availableAfter < 0 && watchedType !== "unpaid" && watchedType !== "sick";

  const docRequired = documentRequirement?.requiresDocument ?? false;
  const docMissing = docRequired && !selectedFile;

  useEffect(() => {
    if (!docRequired && selectedFile) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [docRequired, selectedFile]);

  useEffect(() => {
    if (!replacementCandidatesLoaded || !watchedReplacementEmployeeId) return;
    const stillAvailable = replacementCandidates.some(
      (employee) => String(employee.id) === String(watchedReplacementEmployeeId),
    );
    if (!stillAvailable) setValue("replacementEmployeeId", "");
  }, [
    replacementCandidates,
    replacementCandidatesLoaded,
    setValue,
    watchedReplacementEmployeeId,
  ]);

  // ── Mutation ────────────────────────────────────────────────────────────────

  const submitMutation = useMutation({
    mutationFn: (fd: FormData) => apiSubmitLeaveRequest(fd),
    onSuccess: () => {
      toast.success(t("leave.requestSubmitted"));
      queryClient.invalidateQueries({ queryKey: ["myLeaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["myLeaveBalance"] });
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["notificationCount"] });
      handleClose();
    },
    onError: (err: Error) => {
      toast.error(getLeaveApiErrorMessage(t, err, "leave.submitFailed"));
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function handleClose() {
    reset();
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onOpenChange(false);
  }

  function onSubmit(values: FormValues) {
    if (docMissing) {
      toast.error(t("leave.supportingDocumentRequired"));
      return;
    }
    if (balanceExceeded) {
      toast.error(t("leave.insufficientBalance", {
        available: balanceForType?.availableNow ?? 0,
        requested: workingDays,
      }));
      return;
    }

    const fd = new FormData();
    fd.append("leaveType", values.leaveType);
    fd.append("startDate", format(values.startDate, "yyyy-MM-dd"));
    fd.append("endDate", format(values.endDate, "yyyy-MM-dd"));
    if (values.replacementEmployeeId && values.replacementEmployeeId !== "none")
      fd.append("replacementEmployeeId", values.replacementEmployeeId);
    if (values.notes) fd.append("notes", values.notes);
    if (selectedFile) fd.append("document", selectedFile);

    submitMutation.mutate(fd);
  }

  const today = startOfDay(new Date());
  const availableReplacementEmployees = replacementCandidates.filter(
    (e) => String(e.id) !== String(currentEmployeeId),
  );

  const inputCls = "w-full rounded-xl border border-[#d0d5e8] h-14 bg-white dark:bg-card px-4 text-sm text-foreground flex items-center justify-between transition-colors";

  function formatAssignmentDate(dateStr: string) {
    try {
      return format(new Date(`${dateStr}T00:00:00`), "d MMM yyyy", { locale: dateLocale });
    } catch {
      return dateStr;
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5" />
            {t("leave.requestLeave")}
          </DialogTitle>
          <DialogDescription>
            {t("leave.requestLeaveDescription")}
          </DialogDescription>
        </DialogHeader>

        {user?.accessMode === "limited" && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="ml-2">
              {t("leave.accessMode.limitedSubmitBanner")}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">

            {/* Leave Type */}
            <div className="md:col-span-2">
              <FloatWrapper
                label={t("leave.leaveType")}
                required
                error={errors.leaveType?.message}
              >
                <Controller
                  control={control}
                  name="leaveType"
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger
                        className={cn(
                          "w-full rounded-xl border border-[#d0d5e8] h-14 px-4",
                          errors.leaveType && "border-destructive",
                        )}
                        disabled={user?.accessMode === "limited"}
                      >
                        <SelectValue placeholder={t("leave.selectLeaveType")} />
                      </SelectTrigger>
                      <SelectContent>
                        {leaveTypes.map((lt) => {
                          const bal = balances.find((b) => b.leaveType === lt.name);
                          return (
                            <SelectItem key={lt.name} value={lt.name}>
                              <span className="flex items-center gap-2">
                                <span
                                  className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: lt.color }}
                                />
                                {t(`leave.types.${lt.name}`, { defaultValue: lt.label })}
                                {bal && bal.countsAgainstBalance && lt.name !== "special" && (
                                  <span className="text-xs text-muted-foreground ml-1">
                                    ({bal.availableNow} {t("leave.days").toLowerCase()})
                                  </span>
                                )}
                                {bal && (!bal.countsAgainstBalance || lt.name === "special") && (
                                  <span className="text-xs text-muted-foreground ml-1">
                                    ({bal.usedDays} {t("leave.days").toLowerCase()} {t("leave.approvedDaysIncludingUpcoming")})
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                />
              </FloatWrapper>
            </div>

            {/* Start Date */}
            <div>
              <FloatWrapper
                label={t("leave.startDate")}
                required
                error={errors.startDate?.message}
              >
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field }) => (
                    <Popover open={startOpen} onOpenChange={setStartOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          disabled={user?.accessMode === "limited"}
                          className={cn(inputCls, (!field.value || user?.accessMode === "limited") && "text-muted-foreground/40", errors.startDate && "border-destructive")}
                        >
                          {field.value
                            ? format(field.value, "d MMM yyyy", { locale: dateLocale })
                            : t("leave.pickDate")}
                          <CalendarIcon className="h-4 w-4 opacity-50 shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(d) => { field.onChange(d); setStartOpen(false); }}
                          disabled={(d) => isBefore(d, today)}
                          locale={dateLocale}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                />
              </FloatWrapper>
            </div>

            {/* End Date */}
            <div>
              <FloatWrapper
                label={t("leave.endDate")}
                required
                error={errors.endDate?.message}
              >
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <Popover open={endOpen} onOpenChange={setEndOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          disabled={user?.accessMode === "limited"}
                          className={cn(inputCls, (!field.value || user?.accessMode === "limited") && "text-muted-foreground/40", errors.endDate && "border-destructive")}
                        >
                          {field.value
                            ? format(field.value, "d MMM yyyy", { locale: dateLocale })
                            : t("leave.pickDate")}
                          <CalendarIcon className="h-4 w-4 opacity-50 shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(d) => { field.onChange(d); setEndOpen(false); }}
                          disabled={(d) => isBefore(d, watchedStart ?? today)}
                          locale={dateLocale}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                />
              </FloatWrapper>
            </div>

            {/* Balance preview */}
            {watchedStart && watchedEnd && workingDays >= 0 && (
              <div className="md:col-span-2 rounded-xl border border-[#e8eaf0] bg-muted/30 px-4 py-3 text-sm space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t("leave.workingDaysRequested")}</span>
                  <span className="font-semibold text-foreground">{workingDays}</span>
                </div>
                {balanceForType && isBalanceBasedSelectedType && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t("leave.currentlyAvailable")}</span>
                      <span className="font-medium">{balanceForType.availableNow}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-[#e8eaf0] pt-2">
                      <span className="text-muted-foreground">{t("leave.afterRequest")}</span>
                      <span className={cn("font-semibold", availableAfter! < 0 ? "text-destructive" : "text-green-600")}>
                        {availableAfter}
                      </span>
                    </div>
                  </>
                )}
                {balanceForType && !isBalanceBasedSelectedType && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t("leave.approvedDaysIncludingUpcoming")}</span>
                      <span className="font-medium">{balanceForType.usedDays} {t("leave.days").toLowerCase()}</span>
                    </div>
                    {balanceForType.pendingDays > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t("leave.pending", { defaultValue: "Pending" })}</span>
                        <span className="font-medium">{balanceForType.pendingDays} {t("leave.days").toLowerCase()}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-t border-[#e8eaf0] pt-2">
                      <span className="text-muted-foreground">{t("leave.limit", { defaultValue: "Limit" })}</span>
                      <span className="font-semibold text-green-600">{t("leave.unlimited", { defaultValue: "No limit" })}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Balance exceeded warning */}
            {balanceExceeded && (
              <div className="md:col-span-2">
                <Alert variant="destructive">
                  <AlertDescription>
                    {t("leave.insufficientBalance", {
                      available: balanceForType?.availableNow ?? 0,
                      requested: workingDays,
                    })}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {overlappingReplacementAssignments.length > 0 && (
              <div className="md:col-span-2">
                <Alert className="border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="ml-2 space-y-2">
                    {overlappingReplacementAssignments.length === 1 ? (
                      <p>
                        {t("leave.replacementOverlapWarning", {
                          employeeName: overlappingReplacementAssignments[0].employeeName,
                          startDate: formatAssignmentDate(overlappingReplacementAssignments[0].startDate),
                          endDate: formatAssignmentDate(overlappingReplacementAssignments[0].endDate),
                        })}
                      </p>
                    ) : (
                      <>
                        <p>{t("leave.replacementOverlapWarningIntro")}</p>
                        <ul className="list-disc pl-5 space-y-1">
                          {overlappingReplacementAssignments.map((assignment) => (
                            <li key={assignment.id}>
                              {t("leave.replacementOverlapWarningItem", {
                                employeeName: assignment.employeeName,
                                startDate: formatAssignmentDate(assignment.startDate),
                                endDate: formatAssignmentDate(assignment.endDate),
                              })}
                            </li>
                          ))}
                        </ul>
                        <p>{t("leave.replacementOverlapWarningSubmit")}</p>
                      </>
                    )}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Document upload */}
            {docRequired && (
              <div className="md:col-span-2 space-y-2">
                <p className="text-[13px] font-bold text-foreground">
                  {t("leave.supportingDocument")}
                  <span className="text-red-500 ml-0.5">*</span>
                </p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="h-3 w-3 shrink-0" />
                  {t("leave.supportingDocumentRequired")}
                </p>
                {selectedFile ? (
                  <div className="flex items-center gap-2 rounded-xl border border-[#d0d5e8] px-4 py-3 text-sm bg-white dark:bg-card">
                    <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{selectedFile.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  </div>
                ) : (
                  <div
                    className={cn(
                      "border-2 border-dashed rounded-xl p-6 text-center transition-colors",
                      docMissing && workingDays > 0 ? "border-destructive" : "border-border",
                      user?.accessMode === "limited" ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-accent/30"
                    )}
                    onClick={() => {
                      if (user?.accessMode !== "limited") fileInputRef.current?.click();
                    }}
                  >
                    <Upload className="h-5 w-5 mx-auto mb-1.5 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{t("leave.uploadHint")}</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Replacement Employee */}
            <div className="md:col-span-2">
              <FloatWrapper label={t("leave.replacementEmployee")}>
                <Controller
                  control={control}
                  name="replacementEmployeeId"
                  render={({ field }) => (
                    <EmployeeCombobox
                      employees={availableReplacementEmployees}
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      placeholder={
                        replacementCandidatesFetching
                          ? t("common.loading")
                          : t("leave.replacementPlaceholder")
                      }
                      disabled={user?.accessMode === "limited"}
                    />
                  )}
                />
              </FloatWrapper>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <FloatWrapper label={t("leave.notes")}>
                <textarea
                  {...register("notes")}
                  placeholder={t("leave.notesPlaceholder")}
                  rows={3}
                  disabled={user?.accessMode === "limited"}
                  className="w-full rounded-xl border border-[#d0d5e8] min-h-[90px] resize-none py-4 px-4 text-sm outline-none focus:ring-2 focus:ring-primary/20 bg-white dark:bg-card disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </FloatWrapper>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitMutation.isPending || balanceExceeded || (docMissing && workingDays > 0) || user?.accessMode === "limited"}
              className="gap-2"
            >
              {submitMutation.isPending
                ? <><Loader2 className="h-4 w-4 animate-spin" />{t("leave.submitting")}</>
                : t("leave.submitRequest")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
